import { useAvailablePortfolioTypesState } from "@/utils/hooks/useAvailablePortfolioTypes";
import { useLocalStorage } from "@mantine/hooks";
import { useEffect, useState } from "react";

export type OrgKeyReceivedState = "unset" | "awaiting" | "received";

/**
 * The filters the analysis list applies on a fresh navigation, shared so a
 * page-number lookup made elsewhere matches the list the user lands on.
 *
 * `userId` / `groupId` are list-local and always empty on arrival. Both filters
 * here start at their defaults, which read no differently from "unknown", so
 * `isReady` gates filtering on them.
 */
export const useAnalysisListFilters = () => {
  const { types: regionTypes, isReady: areRegionTypesReady } = useAvailablePortfolioTypesState();
  const [keysReceivedState, setKeysReceivedState] = useLocalStorage<OrgKeyReceivedState>({
    key: "orgKeyReceivedState",
    defaultValue: "unset",
  });

  // useLocalStorage reads storage in its own mount effect; declared after it, so
  // this runs once that read has landed in the same commit.
  const [hasReadKeysState, setHasReadKeysState] = useState(false);
  useEffect(() => setHasReadKeysState(true), []);

  // Until the org keys arrive the user can only reach their own private analyses.
  const isAwaitingOrgKeys = keysReceivedState === "awaiting";

  return {
    regionTypes,
    isPrivate: isAwaitingOrgKeys,
    isReady: areRegionTypesReady && hasReadKeysState,
    keysReceivedState,
    setKeysReceivedState,
  };
};
